'use client';

import { useState } from 'react';
import StyledImage from '@/components/ui/StyledImage';

interface GalleryImage {
    url: string;
    pathname: string;
    category: string;
    /** Description stored with the asset; older rows may not have one. */
    alt?: string | null;
}

interface GalleryGridProps {
    images: GalleryImage[];
}

export default function GalleryGrid({ images }: GalleryGridProps) {
    const [activeCategory, setActiveCategory] = useState('All');

    // Extract unique categories from images
    const categories = ['All', ...Array.from(new Set(images.map(img => img.category))).sort()];

    const filteredImages = activeCategory === 'All'
        ? images
        : images.filter(img => img.category === activeCategory);

    if (images.length === 0) {
        return (
            <div className="text-center py-20 bg-gray-50 rounded-[2.5rem]">
                <div className="w-20 h-20 bg-[#e0f5f2] rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </div>
                <h3 className="text-2xl font-bold text-[var(--color-secondary)] mb-2">No photos yet</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                    We haven't uploaded any photos to the gallery yet. Check back soon to see what we've been up to!
                </p>
            </div>
        );
    }

    return (
        <div>
            {/* Category Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
                {categories.map((category) => (
                    <button
                        key={category}
                        onClick={() => setActiveCategory(category)}
                        className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${activeCategory === category
                                ? 'bg-[var(--color-primary)] text-white shadow-lg scale-105'
                                : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                    >
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                ))}
            </div>

            {/* Image Grid */}
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
                {filteredImages.map((image, i) => (
                    <div key={image.url} className="break-inside-avoid animate-in fade-in zoom-in duration-500">
                        <StyledImage
                            src={image.url}
                            alt={image.alt || `Gallery image - ${image.category}`}
                            width={800}
                            height={600}
                            rounded="xl"
                            shadow={true}
                            rotate={i % 2 === 0 ? 'light' : 'none'}
                            className="w-full mb-6"
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}
